/**
 * Utility functions for file operations
 */

/**
 * Extracts filename from a path containing 'jmk_uploads/'
 * @param {string} path - The file path
 * @returns {string} - The extracted filename or original path if no 'jmk_uploads/' found
 */
export const extractFileNameFromPath = (path) => {
    if (typeof path === 'string' && path.includes('jmk_uploads/')) {
        const parts = path.split('jmk_uploads/');
        return parts[parts.length - 1]; // Get the last part after jmk_uploads/
    }
    return path;
};

/**
 * Safely gets file name from various file object types
 * @param {File|Object|string} file - File object, object with name property, or string path
 * @returns {string} - The file name
 */
export const getFileName = (file) => {
    if (file instanceof File) {
        return file.name;
    }
    if (file && typeof file === 'object' && file.name) {
        return extractFileNameFromPath(file.name);
    }
    // Handle string paths (like from API response)
    if (typeof file === 'string') {
        return extractFileNameFromPath(file);
    }
    return 'Unknown file';
};

/**
 * Processes attachments from API response and converts them to file-like objects
 * @param {Array} attachments - Array of attachments from API response
 * @returns {Array} - Array of processed file-like objects
 */
export const processAttachments = (attachments = []) => {
    return attachments.map(attachment => {
        // If attachment is already a File object, return as is
        if (attachment instanceof File) {
            return attachment;
        }
        
        // If attachment is a string (file path), process it
        if (typeof attachment === 'string') {
            const displayName = extractFileNameFromPath(attachment);
            
            return {
                name: displayName, // Use the extracted filename for display
                originalName: attachment, // Keep the original full path
                size: 0,
                type: 'application/octet-stream',
                url: attachment,
                path: attachment
            };
        }
        
        // If attachment is an object with name property, create a file-like object
        if (attachment && typeof attachment === 'object' && attachment.name) {
            const displayName = extractFileNameFromPath(attachment.name);
            
            return {
                name: displayName, // Use the extracted filename for display
                originalName: attachment.name, // Keep the original full path
                size: attachment.size || 0,
                type: attachment.type || 'application/octet-stream',
                url: attachment.url || attachment.downloadUrl || '',
                // Add other properties that might be needed
                ...attachment
            };
        }
        
        // Fallback for unknown attachment format
        return {
            name: attachment?.name || 'Unknown file',
            originalName: attachment?.name || 'Unknown file',
            size: attachment?.size || 0,
            type: attachment?.type || 'application/octet-stream',
            url: attachment?.url || attachment?.downloadUrl || '',
            ...attachment
        };
    });
};
